import React, { useEffect, useState, useRef, useCallback } from 'react';
import CareerCard from './CareerCard';
import EmployeeHierarchysearch from './EmployeeHierarchysearch';
import "./EmployeeHierarchysearch.css";
import { resetaddCareerCard } from "./AddCareerCardSlice";
import { resetaddCareerCardOtherDetails } from "./AddCareerCardOtherDetailsSlice";
import { resetEmpDetails } from "../CareerCard/CareerCardDetailsSlice";
import { resetEmpOracleDetails } from "../CareerCard/EmpOracleDetailsSlice";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../app/store";
import "bootstrap-icons/font/bootstrap-icons.css";

const Toast: React.FC<{ message: string; type: 'success' | 'error' | 'warning'; onClose: () => void }> = ({
    message,
    type,
    onClose
}) => {
    const bgColor = {
        success: '#28a745',
        error: '#dc3545',
        warning: '#ffc107'
    }[type];

    const textColor = type === 'warning' ? '#000' : '#fff';

    return (
        <div
            style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                backgroundColor: bgColor,
                color: textColor,
                padding: "12px 20px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                zIndex: 10000,
                maxWidth: "400px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                animation: "slideIn 0.3s ease-out"
            }}
        >
            <div style={{ flex: 1, marginRight: "10px" }}>
                {message}
            </div>
            <button
                onClick={onClose}
                style={{
                    background: "transparent",
                    border: "none",
                    color: textColor,
                    fontSize: "18px",
                    cursor: "pointer",
                    padding: "0",
                    lineHeight: "1"
                }}
                aria-label="Close notification"
            >
                <i className="bi bi-x" aria-hidden="true"></i>
            </button>
            <style>
                {`
                  @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                  }
                `}
            </style>
        </div>
    );
};

interface ToastNotification {
    id: number;
    message: string;
    type: 'success' | 'error' | 'warning';
}

const EmployeeSearchWrapper: React.FC<any> = (props : any) => {
    const [searchMode, setSearchMode] = useState<'hierarchy' | 'employee'>('hierarchy');
    const [searchOracleId, setSearchOracleId] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedOracleId, setSelectedOracleId] = useState<string>("");
    const [searchFailed, setSearchFailed] = useState<boolean>(false);
    const [searchSuccess, setSearchSuccess] = useState<boolean>(false);
    const [toasts, setToasts] = useState<ToastNotification[]>([]);
    const [nextToastId, setNextToastId] = useState(1);
    const [careerCardKey, setCareerCardKey] = useState(0);

    const lastSuccessOracleIdRef = useRef<string>("");
    const lastErrorOracleIdRef = useRef<string>("");
    const callbackStateRef = useRef<{
        isSuccessProcessed: boolean;
        isErrorProcessed: boolean;
        currentOracleId: string;
        loadingTimeout: NodeJS.Timeout | null;
    }>({
        isSuccessProcessed: false,
        isErrorProcessed: false,
        currentOracleId: "",
        loadingTimeout: null
    });

    const dispatch = useDispatch<AppDispatch>();

    const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning') => {
        const newToast: ToastNotification = {
            id: nextToastId,
            message,
            type
        };
        setToasts(prev => [...prev, newToast]);
        setNextToastId(prev => prev + 1);

        setTimeout(() => {
            removeToast(newToast.id);
        }, 5000);
    }, [nextToastId]);

    const removeToast = useCallback((id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const validateOracleId = useCallback((oracleId: string): boolean => {
        const trimmed = oracleId.trim();
        return /^\d{6,8}$/.test(trimmed);
    }, []);

    const handleOracleIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '');
        setSearchOracleId(value);
    }, []);

    const clearReduxState = useCallback(() => {
        dispatch(resetEmpDetails());
        dispatch(resetEmpOracleDetails());
        dispatch(resetaddCareerCard());
        dispatch(resetaddCareerCardOtherDetails());
    }, [dispatch]);

    const resetCallbackState = useCallback((oracleId: string = "") => {
        if (callbackStateRef.current.loadingTimeout) {
            clearTimeout(callbackStateRef.current.loadingTimeout);
        }

        callbackStateRef.current = {
            isSuccessProcessed: false,
            isErrorProcessed: false,
            currentOracleId: oracleId,
            loadingTimeout: null
        };
        lastSuccessOracleIdRef.current = "";
        lastErrorOracleIdRef.current = "";
    }, []);

    const handleSearch = useCallback(async () => {
        if (!searchOracleId.trim()) {
            showToast("Please enter an Oracle ID", "warning");
            return;
        }
        
        if (!validateOracleId(searchOracleId.trim())) {
            showToast("Please enter a valid Oracle ID", "warning");
            return;
        }

        const oracleId = searchOracleId.trim();
        
        if (searchMode === 'hierarchy') {
            handleHierarchySearch(oracleId);
        } else {
            handleEmployeeSearch(oracleId);
        }
    }, [searchMode, searchOracleId, showToast, validateOracleId]);

    const handleHierarchySearch = useCallback((oracleId: string) => {

        clearReduxState();
        resetCallbackState(oracleId);

        setSearchFailed(false);
        setSearchSuccess(false);
        setSelectedOracleId(oracleId);
        setCareerCardKey(prev => prev + 1);

        showToast(`Searching hierarchy for Oracle ID: ${oracleId}`, "warning");
    }, [clearReduxState, resetCallbackState, showToast]);

    const handleEmployeeSearch = useCallback((oracleId: string) => {
        clearReduxState();
        resetCallbackState(oracleId);

        setSearchFailed(false);
        setSearchSuccess(false);
        setSelectedOracleId("");
        setLoading(true);
        setCareerCardKey(prev => prev + 1);

        const loadingTimeout = setTimeout(() => {
            const currentState = callbackStateRef.current;
            if (!currentState.isSuccessProcessed && !currentState.isErrorProcessed) {
                setLoading(false);
                showToast(`Search timeout - please try again`, "warning");
            }
        }, 30000);

        callbackStateRef.current.loadingTimeout = loadingTimeout;

        setTimeout(() => {
            setSelectedOracleId(oracleId);
            showToast(`Searching for career card: ${oracleId}`, "warning");
        }, 100);
    }, [clearReduxState, resetCallbackState, showToast]);

    const handleCareerCardSuccess = useCallback((oracleId: string) => {
        if (callbackStateRef.current.loadingTimeout) {
            clearTimeout(callbackStateRef.current.loadingTimeout);
            callbackStateRef.current.loadingTimeout = null;
        }

        const currentState = callbackStateRef.current;

        if (currentState.isSuccessProcessed && currentState.currentOracleId === oracleId) {
            return;
        }

        if (searchSuccess && lastSuccessOracleIdRef.current === oracleId) {
            return;
        }

        callbackStateRef.current.isSuccessProcessed = true;
        callbackStateRef.current.currentOracleId = oracleId;
        callbackStateRef.current.isErrorProcessed = false;
        lastSuccessOracleIdRef.current = oracleId;
        lastErrorOracleIdRef.current = "";

        setLoading(false);
        setSearchSuccess(true);
        setSearchFailed(false);
        showToast(`Career card loaded successfully for Oracle ID: ${oracleId}`, "success");
    }, [searchSuccess, showToast]);

    const handleCareerCardError = useCallback((oracleId: string, error?: string) => {
        if (callbackStateRef.current.loadingTimeout) {
            clearTimeout(callbackStateRef.current.loadingTimeout);
            callbackStateRef.current.loadingTimeout = null;
        }

        const currentState = callbackStateRef.current;

        if (currentState.isErrorProcessed && currentState.currentOracleId === oracleId) {
            return;
        }

        if (searchFailed && lastErrorOracleIdRef.current === oracleId) {
            return;
        }

        callbackStateRef.current.isErrorProcessed = true;
        callbackStateRef.current.currentOracleId = oracleId;
        callbackStateRef.current.isSuccessProcessed = false;
        lastErrorOracleIdRef.current = oracleId;
        lastSuccessOracleIdRef.current = "";

        setLoading(false);
        setSearchSuccess(false);
        setSearchFailed(true);
        setSelectedOracleId("");
        setCareerCardKey(prev => prev + 1);
        showToast(error || `Failed to load career card for Oracle ID: ${oracleId}`, "error");
    }, [searchFailed, showToast]);

    const handleCareerCardLoading = useCallback((isLoading: boolean) => {
        setLoading(isLoading);

        if (!isLoading) {
            const currentState = callbackStateRef.current;
        }
    }, []);

    const clearSearch = useCallback(() => {

        clearReduxState();
        resetCallbackState();

        setSearchOracleId("");
        setSelectedOracleId("");
        setSearchFailed(false);
        setSearchSuccess(false);
        setLoading(false);
        setCareerCardKey(prev => prev + 1);

        showToast("Search cleared", "success");
    }, [clearReduxState, resetCallbackState, showToast]);

    const handleModeChange = useCallback((newMode: 'hierarchy' | 'employee') => {
        if (newMode !== searchMode) {

            clearReduxState();
            resetCallbackState();

            setSearchMode(newMode);
            setSearchOracleId("");
            setSelectedOracleId("");
            setSearchFailed(false);
            setSearchSuccess(false);
            setLoading(false);
            setCareerCardKey(prev => prev + 1);
        }
    }, [searchMode, clearReduxState, resetCallbackState]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !searchSuccess && !loading) {
            handleSearch();
        }
    }, [handleSearch, searchSuccess, loading]);

    useEffect(() => {
        clearReduxState();
        resetCallbackState();

        return () => {
            clearReduxState();
        };
    }, [clearReduxState, resetCallbackState]);

    const handleHierarchySuccess = useCallback((oracleId: string, data?: any) => {

        setLoading(false);
        setSearchSuccess(true);
        setSearchFailed(false);
        showToast(`Hierarchy loaded successfully for Oracle ID: ${oracleId}`, "success");
    }, [showToast]);

    const handleHierarchyError = useCallback((oracleId: string, error?: string) => {

        setLoading(false);
        setSearchSuccess(false);
        setSearchFailed(true);
        setSelectedOracleId("");
        setCareerCardKey(prev => prev + 1);
        showToast(error || `Failed to load hierarchy for Oracle ID: ${oracleId}`, "error");
    }, [showToast]);

    const handleHierarchyLoading = useCallback((isLoading: boolean) => {
        setLoading(isLoading);
    }, []);

    const hasContentToClear = searchSuccess || searchFailed || selectedOracleId || searchOracleId.trim();

    return (
        <div>
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => removeToast(toast.id)}
                />
            ))}

            <div className="card mx-2 mt-3">
                <div className="card-body">
                    <h5 className="card-title mb-3">Employee Search</h5>

                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-semibold">Search Mode:</label>
                            <div className="d-flex gap-4 mt-2">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="searchMode"
                                        id="hierarchyMode"
                                        checked={searchMode === 'hierarchy'}
                                        onChange={() => handleModeChange('hierarchy')}
                                        disabled={loading || searchSuccess}
                                    />
                                    <label className="form-check-label" htmlFor="hierarchyMode">
                                        <i className="bi bi-diagram-3 me-2"></i>
                                        Hierarchy
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="searchMode"
                                        id="employeeMode"
                                        checked={searchMode === 'employee'}
                                        onChange={() => handleModeChange('employee')}
                                        disabled={loading || searchSuccess}
                                    />
                                    <label className="form-check-label" htmlFor="employeeMode">
                                        <i className="bi bi-person-circle me-2"></i>
                                        View/Update Career Card
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row align-items-end">
                        <div className="col-md-8">
                            <label htmlFor="searchOracleId" className="form-label fw-semibold">
                                Employee Oracle ID
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="searchOracleId"
                                placeholder="Enter employee Oracle ID (e.g., 1234567)"
                                value={searchOracleId}
                                onChange={handleOracleIdChange}
                                onKeyPress={handleKeyPress}
                                disabled={loading || searchSuccess}
                                maxLength={8}
                                inputMode="numeric"
                                pattern="[0-9]*"
                            />
                        </div>

                        <div className="col-md-4">
                            <div className="d-flex gap-2">
                                <button
                                    className={`btn ${searchMode === 'hierarchy' ? 'btn-primary' : 'btn-success'}`}
                                    style={{ minWidth: '100px', flex: '1' }}
                                    onClick={handleSearch}
                                    disabled={loading || searchSuccess || !searchOracleId.trim() || !validateOracleId(searchOracleId)}
                                >
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-1" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            Loading
                                        </>
                                    ) : (
                                        <>
                                            <i className={`bi ${searchMode === 'hierarchy' ? 'bi-search' : 'bi-person-search'} me-1`}></i>
                                            Search
                                        </>
                                    )}
                                </button>

                                <button
                                    className={`btn ${hasContentToClear ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                    style={{ minWidth: '80px' }}
                                    onClick={clearSearch}
                                    disabled={loading}
                                    title="Clear search and results"
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-3">
                        <div className={`alert ${searchMode === 'hierarchy' ? 'alert-info' : 'alert-success'} mb-0`}>
                            <i className={`bi ${searchMode === 'hierarchy' ? 'bi-info-circle' : 'bi-lightbulb'} me-2`}></i>
                            {searchMode === 'hierarchy'
                                ? "Search by Oracle ID to view complete organizational hierarchy with bulk PDF export capabilities."
                                : "Search by Oracle ID to view and update individual employee career card details."
                            }
                        </div>
                    </div>
                </div>
            </div>

            {searchSuccess && (
                <div className="alert alert-success mx-2 mt-3" role="alert">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        <div>
                            <h6 className="alert-heading mb-1">
                                {searchMode === 'hierarchy' ? 'Hierarchy Search Loaded' : 'Career Card Loaded'}
                            </h6>
                            <p className="mb-0">
                                Successfully loaded {searchMode === 'hierarchy' ? 'hierarchy' : 'career card'} for Oracle ID: {selectedOracleId}
                            </p>
                        </div>
                        <button
                            className="btn btn-sm btn-outline-success ms-auto"
                            onClick={clearSearch}
                            title="Clear and perform new search"
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            New Search
                        </button>
                    </div>
                </div>
            )}

            {searchFailed && (
                <div className="alert alert-danger mx-2 mt-3" role="alert">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>
                            <h6 className="alert-heading mb-1">Search Failed</h6>
                            <p className="mb-0">
                                Failed to load employee information. Please check the Oracle ID and try again.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {searchMode === 'hierarchy' && selectedOracleId && (
                <div className="mx-2 mt-3">
                    <div className="card">
                        <div className="card-body p-0">
                            <EmployeeHierarchysearch
                                key={`hierarchy-${careerCardKey}-${selectedOracleId}`}
                                oracleId={selectedOracleId}
                                onSuccess={handleHierarchySuccess}
                                onError={handleHierarchyError}
                                onLoading={handleHierarchyLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {searchMode === 'employee' && selectedOracleId && (
                <div className="mx-2 mt-3">
                    <div className="card">
                        <div className="card-body p-0">
                            <CareerCard
                                key={`career-card-${careerCardKey}-${selectedOracleId}`}
                                empId={selectedOracleId}
                                isView={false}
                                isAdminView={true}
                                username={props.username}
                                onSuccess={handleCareerCardSuccess}
                                onError={handleCareerCardError}
                                onLoading={handleCareerCardLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {!loading && !selectedOracleId && !searchFailed && !searchSuccess && (
                <div className="card mx-2 mt-3">
                    <div className="card-body text-center py-5">
                        <i className={`bi ${searchMode === 'hierarchy' ? 'bi-diagram-3' : 'bi-person-circle'} display-4 text-muted mb-3`}></i>
                        <h5 className="text-muted mb-3">
                            {searchMode === 'hierarchy' ? 'Search Employee Hierarchy' : 'Search Employee Career Card'}
                        </h5>
                        <p className="text-muted">
                            {searchMode === 'hierarchy'
                                ? "Enter an employee's Oracle ID to view their complete organizational hierarchy, including all their reportees and sub-reportees."
                                : "Enter an employee's Oracle ID to view and update their career card information."
                            }
                        </p>

                        <div className="row mt-4">
                            {searchMode === 'hierarchy' ? (
                                <>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-person-search text-primary display-6 mb-2"></i>
                                            <h6>Search by Oracle ID</h6>
                                            <small className="text-muted">Find employee by Oracle identifier</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-diagram-3 text-success display-6 mb-2"></i>
                                            <h6>View Hierarchy</h6>
                                            <small className="text-muted">See complete org structure</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-file-earmark-pdf text-danger display-6 mb-2"></i>
                                            <h6>Export PDF</h6>
                                            <small className="text-muted">Generate career cards in bulk</small>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-person-search text-success display-6 mb-2"></i>
                                            <h6>Search by Oracle ID</h6>
                                            <small className="text-muted">Find employee by Oracle identifier</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-eye text-info display-6 mb-2"></i>
                                            <h6>View Details</h6>
                                            <small className="text-muted">See career card information</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center">
                                            <i className="bi bi-pencil-square text-warning display-6 mb-2"></i>
                                            <h6>Update</h6>
                                            <small className="text-muted">Edit career card details</small>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="mt-4">
                            <div className="alert alert-light border">
                                <i className="bi bi-info-circle me-2"></i>
                                <strong>Note:</strong> Both search modes now use Oracle ID for consistency. 
                                Enter Oracle ID to search for employee information.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeSearchWrapper;